import React, { useCallback } from 'react';
import { ListActionSelect, useToggleState } from '@sweetgreen/garnish';

import {
  CreditCardAddModal,
  SingleOptionCreditCardPicker,
} from '@order/components';
import { getCreditCardIcon } from '@order/constants';
import { type PaymentMethod } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import type { CreditCardOption } from '../../../Checkout.utils';
import { useCheckoutContext } from '../../../CheckoutProvider';
import { useCreditCardOptions } from '../../../hooks';

export const ConnectedCheckoutPaymentBlockCreditCardPicker = () => {
  const context = useCheckoutContext();

  return <CheckoutPaymentBlockCreditCardPicker {...context} />;
};

export const CheckoutPaymentBlockCreditCardPicker = (
  props: CreditCardPickerProps,
) => {
  const {
    paymentMethods,
    payload,
    checkoutFieldsValidation,
    handleCreditCardSelected,
    resetCheckoutFieldValidation,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();

  // ─── Credit Card Options ─────────────────────────────────────────────

  const paymentMethodId = payload.paymentMethodId ?? '';
  const { creditCardOptions } = useCreditCardOptions(paymentMethods);
  const selectedCardIcon = getSelectedCardIcon(
    paymentMethodId,
    creditCardOptions,
  );

  // ─── Callbacks ───────────────────────────────────────────────────────

  const {
    value: isShowingAddPaymentMethodModal,
    toggleOn: showAddPaymentMethodModal,
    toggleOff: hideAddPaymentMethodModal,
  } = useToggleState();

  const handleSwitchPaymentMethod = useCallback(
    (id: string | number) => {
      const hasChosenAddPaymentMethod = id === 'ADD';

      if (hasChosenAddPaymentMethod) {
        showAddPaymentMethodModal();

        return;
      }

      const paymentMethod = creditCardOptions.find(
        (option) => option.value === id,
      );

      resetCheckoutFieldValidation('paymentMethodId');
      handleCreditCardSelected({
        paymentMethodId: id as string,
        paymentMethodType: paymentMethod?.paymentMethodType ?? 'BillingAccount',
      });
    },
    [
      creditCardOptions,
      resetCheckoutFieldValidation,
      handleCreditCardSelected,
      showAddPaymentMethodModal,
    ],
  );

  const handlePaymentMethodAdded = useCallback(
    (paymentMethod: PaymentMethod) => {
      resetCheckoutFieldValidation('paymentMethodId');
      handleCreditCardSelected({
        paymentMethodId: paymentMethod.id,
        paymentMethodType: paymentMethod.__typename ?? 'BillingAccount',
      });
      hideAddPaymentMethodModal();
    },
    [
      resetCheckoutFieldValidation,
      handleCreditCardSelected,
      hideAddPaymentMethodModal,
    ],
  );

  // ─── Required Credit Card Notice ─────────────────────────────────────

  const isSelectCardFieldValid = checkoutFieldsValidation.paymentMethodId;
  const selectCardFieldNotice = isSelectCardFieldValid
    ? undefined
    : t('checkout.required-field');

  const shouldShowSingleOptionCreditCardPicker = creditCardOptions.length === 1;

  return (
    <>
      {shouldShowSingleOptionCreditCardPicker ? (
        <SingleOptionCreditCardPicker
          notice={selectCardFieldNotice}
          onPress={showAddPaymentMethodModal}
        />
      ) : (
        <ListActionSelect
          noticePalette="caution"
          submitBtnLabel={t('general.select')}
          modalTitle={t('checkout.payment-method.title')}
          accessibilityLabel={t('checkout.payment-method')}
          notice={selectCardFieldNotice}
          value={paymentMethodId}
          iconName={selectedCardIcon}
          options={creditCardOptions}
          onSelect={handleSwitchPaymentMethod}
        />
      )}

      <CreditCardAddModal
        visible={isShowingAddPaymentMethodModal}
        onRequestClose={hideAddPaymentMethodModal}
        onSuccess={handlePaymentMethodAdded}
      />
    </>
  );
};

// ─── Helpers ───────────────────────────────────────────────────────────

function getSelectedCardIcon(
  paymentMethodId: string,
  options: readonly CreditCardOption[],
) {
  const selectedCard = options.find(({ value }) => value === paymentMethodId);

  return getCreditCardIcon(selectedCard?.cardType);
}

// ─── Types ─────────────────────────────────────────────────────────────

type CreditCardPickerProps = Pick<
  ReturnType<typeof useCheckoutContext>,
  | 'payload'
  | 'paymentMethods'
  | 'checkoutFieldsValidation'
  | 'handleCreditCardSelected'
  | 'resetCheckoutFieldValidation'
>;
